import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  CheckCircle, 
  Save, 
  Loader2, 
  RefreshCw, 
  HelpCircle,
  Eye,
  Shield,
  Send,
  Lock,
  Wrench,
  FileText,
  BadgeAlert,
  DollarSign,
  Receipt,
  FileCheck,
  Printer
} from 'lucide-react';
import { useAuth } from '../App';
import { db, doc, getDoc, setDoc, updateDoc } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'motion/react';

interface NotificationOption {
  id: string;
  label: string;
  description: string;
  category: 'operations' | 'reports' | 'finance' | 'general';
  icon: React.ComponentType<any>;
}

const NOTIFICATION_OPTIONS: NotificationOption[] = [
  {
    id: 'task_assigned',
    label: 'Task Assignment',
    description: 'Alert when a task is assigned, updated, or re-scheduled for you.',
    category: 'operations',
    icon: Wrench,
  },
  {
    id: 'report_submitted',
    label: 'Report Review Requests',
    description: 'Get notified when technical or survey reports are submitted for your approval (Managers / HODs).',
    category: 'reports',
    icon: FileText,
  },
  {
    id: 'report_approved',
    label: 'Report Approved',
    description: 'Instant notification when your submitted work report is approved by management.',
    category: 'reports',
    icon: FileCheck,
  },
  {
    id: 'report_rejected',
    label: 'Report Rejected',
    description: 'Alert when your submitted work report has been returned or requires correction.',
    category: 'reports',
    icon: BadgeAlert,
  },
  {
    id: 'feedback_received',
    label: 'Customer Feedback',
    description: 'Receive details when feedback is submitted by clients regarding your completed tasks.',
    category: 'general',
    icon: MessageSquare,
  },
  {
    id: 'invoice_due',
    label: 'Invoice & Due Warnings',
    description: 'Reminders about pending, upcoming, or overdue invoices and billing status.',
    category: 'finance',
    icon: Receipt,
  },
  {
    id: 'cash_requisition',
    label: 'Cash Requisition Alerts',
    description: 'Notifications for submission, approval, or disbursement of cash requisitions.',
    category: 'finance',
    icon: DollarSign,
  }
];

interface SettingValue {
  inApp: boolean;
  email: boolean;
}

interface UserSettingsState {
  [key: string]: SettingValue | any;
  customEmail: string;
}

const UserSettings: React.FC = () => {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState<UserSettingsState>({
    customEmail: '',
    printOrientation: 'portrait',
    printShowHeader: true,
  });

  // Load user settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;
      try {
        const docRef = doc(db, 'userNotificationSettings', user.uid);
        const docSnap = await getDoc(docRef);

        const initialSettings: UserSettingsState = {
          customEmail: profile?.email || user.email || '',
          printOrientation: 'portrait',
          printShowHeader: true,
        };

        // Pre-populate defaults
        NOTIFICATION_OPTIONS.forEach(opt => {
          initialSettings[opt.id] = { inApp: true, email: false };
        });

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Merge with defaults
          NOTIFICATION_OPTIONS.forEach(opt => {
            if (data[opt.id]) {
              initialSettings[opt.id] = {
                inApp: typeof data[opt.id].inApp === 'boolean' ? data[opt.id].inApp : true,
                email: typeof data[opt.id].email === 'boolean' ? data[opt.id].email : false,
              };
            }
          });
          if (data.customEmail) {
            initialSettings.customEmail = data.customEmail;
          }
          if (data.printOrientation) {
            initialSettings.printOrientation = data.printOrientation;
          }
          if (data.printShowHeader !== undefined) {
            initialSettings.printShowHeader = data.printShowHeader;
          }
        }
        setSettings(initialSettings);
      } catch (error) {
        console.error('Error fetching notification settings:', error);
        toast.error('Failed to load notification settings.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [user, profile]);

  const handleToggle = (optionId: string, channel: 'inApp' | 'email') => {
    setSettings(prev => {
      const current = prev[optionId] || { inApp: true, email: false };
      return {
        ...prev,
        [optionId]: {
          ...current,
          [channel]: !current[channel],
        }
      };
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const docRef = doc(db, 'userNotificationSettings', user.uid);
      
      const payload: any = {
        updatedAt: Timestamp.now(),
        customEmail: settings.customEmail,
        printOrientation: settings.printOrientation || 'portrait',
        printShowHeader: settings.printShowHeader !== undefined ? settings.printShowHeader : true,
      };

      NOTIFICATION_OPTIONS.forEach(opt => {
        payload[opt.id] = settings[opt.id] || { inApp: true, email: false };
      });

      await setDoc(docRef, payload, { merge: true });
      toast.success('Notification & print preferences updated successfully!');
    } catch (error: any) {
      console.error('Error saving notification preferences:', error);
      toast.error('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all notification and print preferences to system defaults?')) {
      setSettings(prev => {
        const reset: UserSettingsState = {
          ...prev,
          customEmail: profile?.email || user?.email || '',
          printOrientation: 'portrait',
          printShowHeader: true,
        };
        NOTIFICATION_OPTIONS.forEach(opt => {
          reset[opt.id] = { inApp: true, email: false };
        });
        return reset;
      });
      toast.success('Settings reset to defaults locally. Save to update.');
    }
  };

  const handleSendTestNotification = async () => {
    if (!user) return;
    setTesting(true);
    try {
      // Create a test in-app and email notification directly using our function
      const testEmail = settings.customEmail || profile?.email || user.email;
      
      // We will simulate calling a test notification
      const hasEmailEnabled = Object.values(settings).some((val: any) => val && val.email === true);
      
      toast.promise(
        new Promise(async (resolve, reject) => {
          try {
            // Write a notification alert in notifications
            const notifyPayload = {
              userId: user.uid,
              title: "Test System Notification",
              message: "This is a validation test triggered from your Notification Preferences panel.",
              type: "general",
              read: false,
              createdAt: Timestamp.now()
            };
            
            // Write to database
            const { addDoc, collection } = await import('firebase/firestore');
            await addDoc(collection(db, 'notifications'), notifyPayload);

            // Send test email
            const emailResponse = await fetch('/api/send-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: testEmail,
                subject: "🔔 Bright Technical Systems - Notification Settings Test",
                body: `Hello ${profile?.displayName || 'Employee'},\n\nThis is a test notification to verify your email destination is working properly.\n\nDestination Email: ${testEmail}\nUser ID: ${user.uid}\nTime Sent: ${new Date().toLocaleString()}\n\nIf you received this message, your email notifications are integrated successfully!\n\nBest Regards,\nBright Technical Systems Notification Engine`
              })
            });

            if (!emailResponse.ok) {
              const resJson = await emailResponse.json();
              throw new Error(resJson.error || 'Failed to trigger test email');
            }
            resolve({ hasEmail: true });
          } catch (err) {
            reject(err);
          }
        }),
        {
          loading: 'Sending test alerts to destination channels...',
          success: 'Test in-app notification & verification email sent!',
          error: (err) => `Verification alert limit: ${err.message}`
        }
      );
    } catch (error: any) {
      console.error('Error sending test notification:', error);
      toast.error('Could not send test alerts: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  const categories = {
    operations: { name: 'Work Operations', color: 'text-blue-600 bg-blue-50' },
    reports: { name: 'Reports & Quality Assurance', color: 'text-violet-600 bg-violet-50' },
    finance: { name: 'Billing & Cash Flow', color: 'text-emerald-600 bg-emerald-50' },
    general: { name: 'Interactions & General', color: 'text-slate-600 bg-slate-50' }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-10 h-10 animate-spin text-blue-900" />
        <p className="font-bold text-slate-500 text-sm tracking-wide">LOADING PREFERENCES Center...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header and Brand */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }} 
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
             <Bell className="w-8 h-8 text-blue-800" />
             Notification Preferences
          </h1>
          <p className="text-slate-500 font-medium italic">Configure real-time in-app alerts and official email routing for specific modules</p>
        </div>
        
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-2xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Defaults
          </button>
          
          <button
            type="button"
            onClick={handleSendTestNotification}
            disabled={testing}
            className="px-5 py-2.5 bg-slate-900 text-white hover:bg-slate-800 rounded-2xl font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Test Delivery
          </button>
        </div>
      </motion.div>

      <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Toggle Controls Form */}
        <div className="lg:col-span-2 space-y-8">
          
          {Object.entries(categories).map(([catId, catInfo]) => {
            const items = NOTIFICATION_OPTIONS.filter(opt => opt.category === catId);
            if (items.length === 0) return null;

            return (
              <motion.div 
                key={catId}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${catInfo.color}`}>
                    {catInfo.name}
                  </span>
                </div>

                <div className="divide-y divide-slate-100 space-y-6">
                  {items.map((opt, idx) => {
                    const value = settings[opt.id] || { inApp: true, email: false };
                    const OptIcon = opt.icon;

                    return (
                      <div key={opt.id} className={`flex flex-col md:flex-row md:items-center justify-between gap-4 pt-6 ${idx === 0 ? 'pt-0 border-t-0' : 'border-t border-slate-100'}`}>
                        <div className="flex items-start gap-4 max-w-md">
                          <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center text-slate-500 shrink-0 mt-0.5">
                            <OptIcon className="w-5 h-5 text-blue-900" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900 text-sm leading-snug">{opt.label}</h4>
                            <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed">{opt.description}</p>
                          </div>
                        </div>

                        {/* Interactive toggle channels */}
                        <div className="flex items-center gap-6 self-start md:self-auto pl-14 md:pl-0">
                          
                          {/* In App Alert Toggle */}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggle(opt.id, 'inApp')}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                value.inApp ? 'bg-blue-800' : 'bg-slate-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  value.inApp ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">In-App</span>
                          </div>

                          {/* Email Notification Toggle */}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggle(opt.id, 'email')}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                value.email ? 'bg-emerald-600' : 'bg-slate-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  value.email ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Email</span>
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-blue-800 text-white py-4 rounded-2.5xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-blue-900 transition-all disabled:opacity-50 shadow-xl shadow-blue-200/50"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Save & Sync Preferences
          </button>

        </div>

        {/* Sidebar Info & Destination Email */}
        <div className="space-y-6">
          <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-6">
            <h3 className="text-base font-bold text-slate-900">Delivery Channel</h3>
            <p className="text-xs text-slate-500 font-medium leading-relaxed">
              Define the email address where your automated notification digests should be delivered. By default, it uses your registered login email.
            </p>

            <div className="space-y-2">
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Routing Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={settings.customEmail}
                  onChange={(e) => setSettings({ ...settings, customEmail: e.target.value })}
                  placeholder="Enter alert email address"
                  required
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-sm text-slate-700"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
              <div>
                <h5 className="font-bold text-slate-800 text-xs">How this works</h5>
                <p className="text-[10px] text-slate-500 font-medium leading-relaxed mt-1">
                  Enabling Email notifications triggers a background SMTP worker to forward operational logs directly to your inbox so you never miss an appointment or correction request.
                </p>
              </div>
            </div>
          </div>

          {/* Print Preferences Section */}
          <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-6">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-800" />
              Print Preferences
            </h3>
            <p className="text-xs text-slate-500 font-medium leading-relaxed">
              Configure your default layout settings for physical storage and hardcopy formatting of Technical Reports.
            </p>

            <div className="space-y-4">
              {/* Paper Orientation */}
              <div className="space-y-1.5">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest block">Paper Orientation</label>
                <select
                  value={settings.printOrientation || 'portrait'}
                  onChange={(e) => setSettings({ ...settings, printOrientation: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-xs text-slate-700"
                >
                  <option value="portrait">Portrait (Standard)</option>
                  <option value="landscape">Landscape (Wide Tables)</option>
                </select>
              </div>

              {/* Show Header */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-slate-700">Display Letterhead Header</span>
                  <span className="text-[10px] text-slate-400">Show company branding on report printouts</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, printShowHeader: !settings.printShowHeader })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    settings.printShowHeader ? 'bg-blue-800' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.printShowHeader ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-6 md:p-8 rounded-[2.5rem] border border-blue-100 space-y-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-blue-800" />
              <h4 className="font-bold text-blue-900 text-sm">Security Controls</h4>
            </div>
            <p className="text-xs text-blue-800/80 font-medium leading-relaxed">
              Your preferences are held securely. Admin profiles can configure central alerts inside Settings, but each employee maintains individual sovereignty over their chosen communications.
            </p>
          </div>
        </div>

      </form>
    </div>
  );
};

export default UserSettings;
